/**
 * Pro Shipping Configuration and Utilities
 * Handles free shipping threshold, monthly credit logic, and $30/month benefit cap
 */

// Pro Shipping Configuration
export const PRO_SHIPPING_CONFIG = {
  FREE_SHIPPING_THRESHOLD_CENTS: 7500, // $75.00
  MONTHLY_CREDIT_CENTS: 1500, // $15.00
  MONTHLY_BENEFIT_CAP_CENTS: 3000, // $30.00 total cap per month
  CREDIT_PERIOD_DAYS: 30,
  CREDITS_ROLLOVER: false, // Unused credits expire
};

export interface ProShippingBenefits {
  freeShippingThreshold: number;
  monthlyShippingCredit: number;
  monthlyBenefitCap: number;
}

export interface ShippingDiscount {
  originalShipping: number;
  discountAmount: number;
  discountType: 'free_shipping' | 'credit_applied' | 'partial_credit' | 'none' | 'capped';
  creditUsed: number;
  creditRemaining: number;
  finalShipping: number;
  capUsed: number;
  capRemaining: number;
}

/**
 * Create a no-discount result
 */
function noDiscountResult(shippingCents: number, availableCreditCents: number, capRemaining: number): ShippingDiscount {
  return {
    originalShipping: shippingCents,
    discountAmount: 0,
    discountType: 'none',
    creditUsed: 0,
    creditRemaining: availableCreditCents,
    finalShipping: shippingCents,
    capUsed: 0,
    capRemaining,
  };
}

/**
 * Calculate Pro shipping discount with $30/month benefit cap
 * @param subtotalCents - Order subtotal in cents
 * @param shippingCents - Original shipping cost in cents
 * @param isPro - Whether the user is a Pro subscriber
 * @param availableCreditCents - User's available shipping credit in cents
 * @param benefitsUsedCents - Total benefits already used this period (for cap tracking)
 * @param capCents - Monthly cap in cents (defaults to $30)
 */
export function calculateCappedShippingDiscount(
  subtotalCents: number,
  shippingCents: number,
  isPro: boolean,
  availableCreditCents: number = 0,
  benefitsUsedCents: number = 0,
  capCents: number = PRO_SHIPPING_CONFIG.MONTHLY_BENEFIT_CAP_CENTS
): ShippingDiscount {
  // Calculate remaining cap
  const remainingCap = Math.max(0, capCents - benefitsUsedCents);

  // Default response - no discount
  if (!isPro) {
    return noDiscountResult(shippingCents, availableCreditCents, remainingCap);
  }

  // If cap is exhausted, no discount available
  if (remainingCap <= 0) {
    return {
      originalShipping: shippingCents,
      discountAmount: 0,
      discountType: 'capped',
      creditUsed: 0,
      creditRemaining: availableCreditCents,
      finalShipping: shippingCents,
      capUsed: 0,
      capRemaining: 0,
    };
  }

  // Check if order qualifies for free shipping (>= $75)
  if (subtotalCents >= PRO_SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD_CENTS) {
    // Free shipping - but capped
    const discountAmount = Math.min(shippingCents, remainingCap);
    const finalShipping = shippingCents - discountAmount;
    
    return {
      originalShipping: shippingCents,
      discountAmount,
      discountType: discountAmount < shippingCents ? 'capped' : 'free_shipping',
      creditUsed: 0,
      creditRemaining: availableCreditCents,
      finalShipping,
      capUsed: discountAmount,
      capRemaining: remainingCap - discountAmount,
    };
  }

  // Check if user has shipping credit available
  if (availableCreditCents > 0) {
    // Credit applied - but capped
    const maxDiscount = Math.min(availableCreditCents, shippingCents, remainingCap);
    const finalShipping = shippingCents - maxDiscount;
    const creditRemaining = availableCreditCents - maxDiscount;

    let discountType: ShippingDiscount['discountType'] = 'credit_applied';
    if (maxDiscount < shippingCents && maxDiscount === remainingCap) {
      discountType = 'capped';
    } else if (maxDiscount < shippingCents) {
      discountType = 'partial_credit';
    }

    return {
      originalShipping: shippingCents,
      discountAmount: maxDiscount,
      discountType,
      creditUsed: maxDiscount,
      creditRemaining,
      finalShipping,
      capUsed: maxDiscount,
      capRemaining: remainingCap - maxDiscount,
    };
  }

  return noDiscountResult(shippingCents, availableCreditCents, remainingCap);
}

/**
 * Legacy function for backward compatibility - wraps new capped version
 * @deprecated Use calculateCappedShippingDiscount instead
 */
export function calculateProShippingDiscount(
  subtotalCents: number,
  shippingCents: number,
  isPro: boolean,
  availableCreditCents: number = 0,
  benefitsUsedCents: number = 0
): ShippingDiscount {
  return calculateCappedShippingDiscount(
    subtotalCents,
    shippingCents,
    isPro,
    availableCreditCents,
    benefitsUsedCents
  );
}

/**
 * Format shipping discount for display
 */
export function formatShippingDiscount(discount: ShippingDiscount): string {
  switch (discount.discountType) {
    case 'free_shipping':
      return 'FREE (Pro: orders $75+)';
    case 'credit_applied':
      return `$0.00 (Pro Credit: -$${(discount.creditUsed / 100).toFixed(2)})`;
    case 'partial_credit':
      return `$${(discount.finalShipping / 100).toFixed(2)} (Credit: -$${(discount.creditUsed / 100).toFixed(2)})`;
    case 'capped':
      if (discount.discountAmount > 0) {
        return `$${(discount.finalShipping / 100).toFixed(2)} (Cap reached: -$${(discount.discountAmount / 100).toFixed(2)})`;
      }
      return `$${(discount.originalShipping / 100).toFixed(2)} (Monthly cap reached)`;
    default:
      return `$${(discount.originalShipping / 100).toFixed(2)}`;
  }
}

/**
 * Check if an order qualifies for free shipping
 */
export function qualifiesForFreeShipping(subtotalCents: number, isPro: boolean): boolean {
  return isPro && subtotalCents >= PRO_SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD_CENTS;
}

/**
 * Get the amount needed to reach free shipping threshold
 */
export function getAmountToFreeShipping(subtotalCents: number, isPro: boolean): number {
  if (!isPro) return -1; // Not applicable
  if (subtotalCents >= PRO_SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD_CENTS) return 0;
  return PRO_SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD_CENTS - subtotalCents;
}

/**
 * Get remaining monthly benefit cap
 */
export function getRemainingBenefitCap(benefitsUsedCents: number): number {
  return Math.max(0, PRO_SHIPPING_CONFIG.MONTHLY_BENEFIT_CAP_CENTS - benefitsUsedCents);
}
